import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { formatCurrency, type CurrencyFormatOptions } from "../u2000Core/tools/U2000CurrencyUtils";
import { doInitLU, luHandleAppiError, u3125NormalizeInitLUTaxRates, type U3125InitLU } from "./U3125Service";
import { U3125ContextProvider } from "./U3125Context";
import { U3125InitCreateInvoiceDialogProvider } from "./U3125InitCreateInvoiceDialogContext";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3125";

/** Options de formatage monétaire U3125 (saisie et affichage). */
export const U3125_CURRENCY_FORMAT: CurrencyFormatOptions = {
  currencySymbol: "$",
  symbolPosition: "left",
  decimalSeparator: ".",
  thousandSeparator: " ",
  decimalDigits: 2,
  allowNegative: true,
};

export function u3125FormatCurrency(value: unknown): string {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return formatCurrency(n, U3125_CURRENCY_FORMAT);
}

export const U3125ROLES = {
  READ: "U3125:READ",
  ADD: "U3125:ADD",
  EDIT: "U3125:EDIT",
  DELETE: "U3125:DELETE",
  AUDITS: "U3125:AUDITS",
  EXPORT: "U3125:EXPORT",
  VIEWTECLINES: "U3125:VIEWTECLINES",
} as const;
export type U3125ROLES = (typeof U3125ROLES)[keyof typeof U3125ROLES];

const defaultInit: U3125InitLU = {
  launchSearch: false,
  hideSearch: false,
  nbRecordPerPage: 25,
  admIncludeSubProject: null,
  enableInvoicePdfAndLines: false,
  allowInvoiceAboveBudget: false,
  invoiceTypes: [],
  invoiceStatuses: [],
  documentNatures: [],
  taxRates: [],
  paymentTypes: [],
};

const U3125: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3125InitLU>(defaultInit);

  useEffect(() => {
    i18next.loadNamespaces(["u3125"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3125InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3125InitLU) => {
      setInitLUData({
        ...initLU,
        taxRates: u3125NormalizeInitLUTaxRates(initLU.taxRates),
      });
      setInitLUReady(true);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3125ContextProvider initLU={initLUData}>
      <U3125InitCreateInvoiceDialogProvider>
        <Outlet />
      </U3125InitCreateInvoiceDialogProvider>
    </U3125ContextProvider>
  );
};

export default U3125;
